import React from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, RefreshCw, XCircle } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loading } from "@/components/common/Loading";
import { OaSpend } from "./OaSpend";

// Otter Assist admin — Overview / System Health (OAD-127).
// Every number comes from telemetry that already exists. A Moderator never receives provider spend.

const TONE = {
  healthy: { cls: "bg-emerald-50 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
  warning: { cls: "bg-amber-50 text-amber-900 border-amber-300", Icon: AlertTriangle },
  critical: { cls: "bg-red-50 text-red-800 border-red-300", Icon: XCircle },
};

function StateBadge({ state, testId }) {
  const tone = TONE[state] || TONE.healthy;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-[700] uppercase tracking-wider ${tone.cls}`}
      data-testid={testId}>
      <tone.Icon className="h-3 w-3" /> {state}
    </span>
  );
}

function Panel({ title, state, testId, children }) {
  return (
    <Card className="p-4" data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-[640] text-foreground">{title}</h3>
        {state ? <StateBadge state={state} testId={`${testId}-state`} /> : null}
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">{children}</dl>
    </Card>
  );
}

function Row({ label, value, testId }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-[600] tabular-nums" data-testid={testId}>
        {value === null || value === undefined ? "—" : String(value)}
      </dd>
    </>
  );
}

export function OaOverview() {
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["oa-admin-health"],
    queryFn: async () => (await api.get("/admin/otter-assist/health")).data,
    refetchInterval: 60000,
  });

  if (isLoading) return <Loading />;
  if (isError || !data) {
    return <p className="text-sm text-muted-foreground" data-testid="oa-health-error">
      System health is unavailable right now.
    </p>;
  }

  return (
    <div className="space-y-4" data-testid="oa-admin-overview">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-[640]">Is Otter Assist working right now?</span>
          <StateBadge state={data.overall} testId="oa-health-overall" />
        </div>
        <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}
          data-testid="oa-health-refresh">
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <Panel title="Assists" testId="oa-health-lifecycle">
          <Row label="Open for help" value={data.lifecycle?.active} testId="oa-health-active" />
          {Object.entries(data.lifecycle?.by_status || {}).slice(0, 7).map(([k, v]) => (
            <Row key={k} label={k.replace(/_/g, " ")} value={v} />
          ))}
        </Panel>

        <Panel title="Matching" state={data.matching?.state} testId="oa-health-matching">
          <Row label="Running" value={data.matching?.running} />
          <Row label={`Stalled (> ${data.matching?.stalled_after_minutes}m)`}
            value={data.matching?.stalled} testId="oa-health-stalled" />
          <Row label="Failed runs (24h)" value={data.matching?.failed_runs_24h} />
          <Row label="Assists in matching failure" value={data.matching?.assists_matching_failed} />
        </Panel>

        <Panel title="Delivery" state={data.delivery?.state} testId="oa-health-delivery">
          <Row label="Queued" value={data.delivery?.queued} testId="oa-health-queued" />
          <Row label="Oldest queued (min)" value={data.delivery?.oldest_queued_minutes} />
          <Row label="Overdue waves" value={data.delivery?.overdue_waves} />
          <Row label="Failed (24h)" value={data.delivery?.failed_24h} />
        </Panel>

        <Panel title="Notifications" state={data.notifications?.state} testId="oa-health-notify">
          <Row label="Pending" value={data.notifications?.pending} />
          <Row label="Retrying" value={data.notifications?.retrying} />
          <Row label="Failed" value={data.notifications?.failed} testId="oa-health-notify-failed" />
          <Row label="Outbox backlog" value={data.notifications?.outbox_backlog} />
          <Row label="Dispatch enabled" value={data.notifications?.dispatch_enabled ? "yes" : "no"} />
        </Panel>

        <Panel title="Email digests" state={data.digests?.state} testId="oa-health-digests">
          <Row label="Sent (24h)" value={data.digests?.sent_24h} />
          <Row label="Empty (24h)" value={data.digests?.empty_24h} />
          <Row label="Failed" value={data.digests?.failed} />
          <Row label="Stuck claims" value={data.digests?.stuck_claims} />
          <Row label="Digests enabled" value={data.digests?.digests_enabled ? "yes" : "no"} />
        </Panel>

        <Panel title="Matching index" state={data.index?.state} testId="oa-health-index">
          {Object.entries(data.index?.documents || {}).map(([k, v]) => (
            <Row key={k} label={k} value={v} />
          ))}
          <Row label="Queue pending" value={data.index?.queue_pending} />
          <Row label="Queue errors" value={data.index?.queue_error} />
        </Panel>

        <Panel title="AI provider" state={data.ai?.state} testId="oa-health-provider">
          <Row label="Chat provider" value={data.provider?.chat_provider} />
          <Row label="Chat model" value={data.provider?.chat_model} />
          <Row label="Chat configured" value={data.provider?.chat_configured ? "yes" : "no"} />
          <Row label="Embedding provider" value={data.provider?.embed_provider} />
          <Row label="Embedding configured" value={data.provider?.embed_configured ? "yes" : "no"} />
          <Row label="Embedding transport" value={data.provider?.embed_transport} />
          <Row label="AI cap usage — today (UTC)" value={data.ai?.calls_today}
            testId="oa-health-ai-calls-today" />
          <Row label="Daily cap" value={data.ai?.daily_cap} />
        </Panel>

        {data.cost ? (
          <Panel title="Provider usage — last 24 hours" testId="oa-health-cost">
            <Row label="Window" value={data.cost.window} />
            <Row label="Provider calls — last 24 hours" value={data.cost.calls}
              testId="oa-health-cost-calls" />
            <Row label="Estimated (USD)" value={data.cost.estimated_usd}
              testId="oa-health-cost-estimate" />
            <dt className="col-span-2 mt-1 text-[11px] italic text-muted-foreground">
              {data.cost.basis}. This is a rolling 24-hour count; the AI cap figure above is the
              current UTC calendar day, so the two numbers use different windows.
            </dt>
          </Panel>
        ) : null}

        <Panel title="Scheduled jobs" testId="oa-health-jobs">
          {(data.jobs || []).map((job) => (
            <Row key={job.job} label={job.job.replace("otter_assist_", "")}
              value={job.last_success_at ? "ok" : (job.last_run_at ? "failing" : "no run yet")}
              testId={`oa-health-job-${job.job}`} />
          ))}
        </Panel>
      </div>

      {data.cost ? <OaSpend /> : null}

      <Card className="p-4" data-testid="oa-health-alerts">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-[640] text-foreground">Active alerts</h3>
          <Badge variant={data.alerts?.enabled ? "default" : "secondary"}
            data-testid="oa-health-alerts-enabled">
            {data.alerts?.enabled ? "alerting on" : "alerting off"}
          </Badge>
        </div>
        <p className="mt-1 text-[11px] text-muted-foreground">
          Staff are paged for a severe processing outage and for sustained terminal
          notification/digest failures only. Everything else stays on this dashboard.
        </p>
        <div className="mt-3 space-y-2">
          {(data.alerts?.alerts || []).map((alert) => (
            <div key={alert.key}
              className="flex items-start justify-between gap-3 rounded-md border px-3 py-2"
              data-testid={`oa-health-alert-${alert.key}`}>
              <div>
                <div className="text-[12px] font-[640] text-foreground">{alert.title}</div>
                <div className="text-[11px] text-muted-foreground">
                  {alert.open
                    ? `open since ${alert.first_seen_at || "—"}`
                    : `clear · ${alert.consecutive} of ${data.alerts?.required_consecutive} consecutive`}
                </div>
              </div>
              <StateBadge state={alert.open ? "critical" : "healthy"}
                testId={`oa-health-alert-${alert.key}-state`} />
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-4" data-testid="oa-health-flags">
        <h3 className="text-sm font-[640]">Feature state</h3>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {(data.flags || []).map((f) => (
            <Badge key={f.key} variant={f.effective ? "default" : "secondary"}
              data-testid={`oa-health-flag-${f.key}`}>
              {f.key} {f.effective ? "on" : "off"}
              {f.source === "runtime" ? " ·  override" : ""}
            </Badge>
          ))}
        </div>
      </Card>
    </div>
  );
}

export default OaOverview;
